import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchUserDebts } from '../../store/slices/usersSlice';
import {
  decreaseInstallmentAmount,
  fullDebtPayment,
  getDebtById,
  increaseInstallmentAmount
} from '../../services/api/debtsApi';
import { Debt, DebtDetails, Installment, User } from '../../types';
import { formatDateToEMEA } from '../../utils/dateFormatter';
import { useTranslation } from '../../hooks/useTranslation';
import './DebtDetailsModal.css';
import { isDebtFullyPaid } from '../../utils/isDebtFullyPaid';

interface DebtDetailsModalProps {
  user: User;
  onClose: () => void;
}

const DebtDetailsModal: React.FC<DebtDetailsModalProps> = ({ user, onClose }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { selectedUserDebts, debtsLoading } = useAppSelector((state) => state.users);
  const { userType } = useAppSelector((state) => state.auth);
  
  const [selectedDebt, setSelectedDebt] = useState<Debt | null>(null);
  const [debtDetails, setDebtDetails] = useState<DebtDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [installmentAmountDrafts, setInstallmentAmountDrafts] = useState<Record<string, string>>({});
  const [actionLoadingKey, setActionLoadingKey] = useState<string | null>(null);
  const [fullPaymentAmount, setFullPaymentAmount] = useState<string>('');
  const [fullPaymentDate, setFullPaymentDate] = useState<string>(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    // Debts are already loaded in UsersTab, but we can reload if needed
    if (user.id) {
      dispatch(fetchUserDebts(user.id));
    }
  }, [user, dispatch]);

  const handleViewDebtDetails = async (debt: Debt) => {
    try {
      setDetailsLoading(true);
      setSelectedDebt(debt);
      setActionError(null);
      setActionSuccess(null);
      setInstallmentAmountDrafts({});
      setActionLoadingKey(null);
      setFullPaymentDate(new Date().toISOString().slice(0, 10));
      setFullPaymentAmount(String(getRemainingBalance(debt) || debt.totalDebt || ''));
      
      // Fetch debt details with installments
      const details = await getDebtById(debt.id.toString());
      setDebtDetails(details);
    } catch (error) {
      console.error('Error fetching debt details:', error);
      // Fallback to null on error
      setDebtDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'MRU' // Adjust currency as needed
    }).format(amount);
  };

  const getRemainingBalance = (debt: Debt): number => {
    return debt.remainingBalance ?? debt.remainingAmount ?? 0;
  };

  const canManageInstallments = userType === 'TWIZE_ADMIN' || userType === 'PARTNER_MANAGER';

  const parsePositiveAmount = (value: string): number | null => {
    const normalized = value.replace(',', '.').trim();
    const num = Number(normalized);
    if (!Number.isFinite(num) || num <= 0) return null;
    return num;
  };

  const getApiErrorMessage = (error: unknown, fallback: string): string => {
    const maybeMessage =
      (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
    return maybeMessage || fallback;
  };

  const refreshDebtDetails = async () => {
    if (!selectedDebt) return;
    const details = await getDebtById(selectedDebt.id.toString());
    setDebtDetails(details);
  };

  const getInstallmentKey = (installment: Installment): string => {
    return installment.id || String(installment.installmentNumber);
  };

  const handleInstallmentUpdate = async (
    installment: Installment,
    direction: 'increase' | 'decrease'
  ) => {
    const installmentId = installment.id;
    const key = getInstallmentKey(installment);
    const draft = installmentAmountDrafts[key] ?? '';
    const amount = parsePositiveAmount(draft);

    setActionError(null);
    setActionSuccess(null);

    if (!installmentId) {
      setActionError(t('debtDetails.actions.installmentIdMissing'));
      return;
    }
    if (!amount) {
      setActionError(t('debtDetails.actions.invalidAmount'));
      return;
    }

    try {
      setActionLoadingKey(`${direction}:${key}`);
      if (direction === 'increase') {
        await increaseInstallmentAmount(installmentId, amount);
        setActionSuccess(t('debtDetails.actions.increaseSuccess'));
      } else {
        await decreaseInstallmentAmount(installmentId, amount);
        setActionSuccess(t('debtDetails.actions.decreaseSuccess'));
      }
      await refreshDebtDetails();
      setInstallmentAmountDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (e) {
      console.error('Installment update failed:', e);
      setActionError(getApiErrorMessage(e, t('debtDetails.actions.updateFailed')));
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleFullPayment = async () => {
    if (!selectedDebt) return;
    setActionError(null);
    setActionSuccess(null);
    const amount = parsePositiveAmount(fullPaymentAmount);
    if (!amount) {
      setActionError(t('debtDetails.actions.invalidAmount'));
      return;
    }
    if (!fullPaymentDate) {
      setActionError(t('debtDetails.actions.invalidDate'));
      return;
    }

    try {
      setActionLoadingKey('fullPayment');
      await fullDebtPayment(selectedDebt.id.toString(), amount, fullPaymentDate);
      setActionSuccess(t('debtDetails.actions.fullPaymentSuccess'));
      await refreshDebtDetails();
    } catch (e) {
      console.error('Full payment failed:', e);
      setActionError(getApiErrorMessage(e, t('debtDetails.actions.fullPaymentFailed')));
    } finally {
      setActionLoadingKey(null);
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'PAID':
        return 'paid';
      case 'PENDING':
        return 'pending';
      case 'OVERDUE':
        return 'overdue';
      default:
        return '';
    }
  };

  const getStatusTranslation = (status: string): string => {
    switch (status) {
      case 'PAID':
        return t('status.paid');
      case 'PENDING':
        return t('status.pending');
      case 'OVERDUE':
        return t('status.overdue');
      default:
        return status;
    }
  };

  const debtFullyPaid =
    selectedDebt && debtDetails ? isDebtFullyPaid(selectedDebt, debtDetails) : false;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('debtDetails.title', { firstName: user.firstName, lastName: user.lastName })}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {selectedDebt ? (
            <div className="payments-view">
              <button 
                className="back-to-debts-button"
                onClick={() => {
                  setSelectedDebt(null);
                  setDebtDetails(null);
                }}
              >
                ← {t('debtDetails.backToDebts')}
              </button>
              
              {detailsLoading ? (
                <div className="loading">{t('debtDetails.loadingInstallments')}</div>
              ) : debtDetails ? (
                <>
                  <div className="installments-header">
                    <div className="header-item">
                      <span className="header-label">{t('debtDetails.totalDebt')}:</span>
                      <span className="header-value">{formatCurrency(debtDetails.totalDebt)}</span>
                    </div>
                    <div className="header-item">
                      <span className="header-label">{t('debtDetails.totalInstallments')}:</span>
                      <span className="header-value">{debtDetails.numberOfInstallments}</span>
                    </div>
                    <div className="header-item">
                      <span className="header-label">{t('debtDetails.firstPaymentDate')}:</span>
                      <span className="header-value">{formatDateToEMEA(debtDetails.firstPaymentDate)}</span>
                    </div>
                  </div>

                  {canManageInstallments && !debtFullyPaid && (
                    <div className="installments-actions">
                      <div className="full-payment">
                        <div className="full-payment-title">{t('debtDetails.actions.fullPaymentTitle')}</div>
                        {selectedDebt && (
                          <div className="full-payment-remaining">
                            {t('debtDetails.actions.remainingToPay')}:&nbsp;
                            <strong>{formatCurrency(getRemainingBalance(selectedDebt))}</strong>
                          </div>
                        )}
                        <div className="full-payment-form">
                          <input
                            className="amount-input"
                            type="text"
                            readOnly
                            value={fullPaymentAmount}
                          />
                          <input
                            className="date-input"
                            type="date"
                            value={fullPaymentDate}
                            onChange={(e) => setFullPaymentDate(e.target.value)}
                          />
                          <button
                            className="primary-action-button"
                            onClick={handleFullPayment}
                            disabled={actionLoadingKey === 'fullPayment'}
                          >
                            {actionLoadingKey === 'fullPayment'
                              ? t('debtDetails.actions.processing')
                              : t('debtDetails.actions.payInFull')}
                          </button>
                        </div>
                      </div>

                      {actionError && <div className="action-error">{actionError}</div>}
                      {actionSuccess && <div className="action-success">{actionSuccess}</div>}
                    </div>
                  )}

                  <div className="table-container">
                    <table className="payments-table">
                      <thead>
                        <tr>
                          <th>{t('debtDetails.table.number')}</th>
                          <th>{t('debtDetails.table.amount')}</th>
                          <th>{t('debtDetails.table.scheduledDate')}</th>
                          <th>{t('debtDetails.table.status')}</th>
                          {canManageInstallments && <th>{t('debtDetails.table.actions')}</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {debtDetails.installments.length === 0 ? (
                          <tr>
                            <td colSpan={canManageInstallments ? 5 : 4} className="no-data">{t('debtDetails.table.noInstallments')}</td>
                          </tr>
                        ) : (() => {
                          const sorted = [...debtDetails.installments].sort((a, b) => a.installmentNumber - b.installmentNumber);
                          const nextEditable = sorted.find((inst) => inst.status !== 'PAID' && !inst.paid && !inst.isPaid);
                          const nextEditableKey = nextEditable ? getInstallmentKey(nextEditable) : null;

                          return sorted.map((installment) => {
                            const key = getInstallmentKey(installment);
                            const isEditable = canManageInstallments && nextEditableKey === key;

                            return (
                              <tr key={key}>
                                <td>{installment.installmentNumber}</td>
                                <td>{formatCurrency(installment.amount)}</td>
                                <td>{formatDateToEMEA(installment.scheduledPaymentDate)}</td>
                                <td>
                                  <span className={`status-badge ${getStatusBadgeClass(installment.status)}`}>
                                    {getStatusTranslation(installment.status)}
                                  </span>
                                </td>
                                {canManageInstallments && (
                                  <td>
                                    {isEditable && (
                                      <div className="installment-action-cell">
                                        <input
                                          className="amount-input"
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={installmentAmountDrafts[getInstallmentKey(installment)] ?? ''}
                                          onChange={(e) => {
                                            const k = getInstallmentKey(installment);
                                            setInstallmentAmountDrafts((prev) => ({ ...prev, [k]: e.target.value }));
                                          }}
                                          placeholder={t('debtDetails.actions.amountPlaceholder')}
                                        />
                                        <div className="installment-action-buttons">
                                          <button
                                            className="secondary-action-button"
                                            onClick={() => handleInstallmentUpdate(installment, 'increase')}
                                            disabled={actionLoadingKey === `increase:${getInstallmentKey(installment)}`}
                                          >
                                            {actionLoadingKey === `increase:${getInstallmentKey(installment)}`
                                              ? t('debtDetails.actions.processing')
                                              : t('debtDetails.actions.increase')}
                                          </button>
                                          <button
                                            className="secondary-action-button danger"
                                            onClick={() => handleInstallmentUpdate(installment, 'decrease')}
                                            disabled={actionLoadingKey === `decrease:${getInstallmentKey(installment)}`}
                                          >
                                            {actionLoadingKey === `decrease:${getInstallmentKey(installment)}`
                                              ? t('debtDetails.actions.processing')
                                              : t('debtDetails.actions.decrease')}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                        </>
              ) : (
                <div className="error-message">{t('debtDetails.failedToLoad')}</div>
              )}
            </div>
          ) : (
            <div className="debts-list">
              {debtsLoading ? (
                <div className="loading">{t('debtDetails.loading')}</div>
              ) : selectedUserDebts.length === 0 ? (
                <div className="no-data">{t('debtDetails.noDebts')}</div>
              ) : (
                <div className="table-container">
                  <table className="debts-table">
                    <thead>
                      <tr>
                        <th>{t('debtDetails.tableDebts.totalDebt')}</th>
                        <th>{t('debtDetails.tableDebts.totalPaid')}</th>
                        <th>{t('debtDetails.tableDebts.remainingBalance')}</th>
                        <th>{t('debtDetails.tableDebts.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedUserDebts.map((debt) => (
                        <tr key={debt.id}>
                          <td>{formatCurrency(debt.totalDebt)}</td>
                          <td>{formatCurrency(debt.totalPaid ?? 0)}</td>
                          <td>{formatCurrency(getRemainingBalance(debt))}</td>
                          <td>
                            <button
                              className="view-payments-button"
                              onClick={() => handleViewDebtDetails(debt)}
                            >
                              {t('debtDetails.tableDebts.viewDetails')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DebtDetailsModal;

